/** @type {Record<'morning' | 'afternoon' | 'evening', readonly string[]>} */
export const ASHLEY_WAKE_GREETINGS = {
  morning: [
    "Good morning. What can I do for you today?",
    "Good Morning.",
    "Morning. What's up?",
  ],
  afternoon: [
    "Good afternoon.",
    "What's up?",
    "Go ahead.",
    "How can I help?",
    "What can I do for you?",
  ],
  evening: [
    "Good evening.",
    "I'm listening.",
    "Need something?",
    "Go ahead.",
    "How can I help?",
  ],
};

let greetingCycleIndex = 0;

/**
 * Resets the greeting cycle index. Intended for tests.
 */
export function resetAshleyGreetingCycle() {
  greetingCycleIndex = 0;
}

/**
 * @param {Date} [date]
 * @returns {'morning' | 'afternoon' | 'evening'}
 */
export function getTimeOfDayPeriod(date = new Date()) {
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) {
    return "morning";
  }

  if (hour >= 12 && hour < 17) {
    return "afternoon";
  }

  return "evening";
}

/**
 * Ashley's spoken opening greeting when voice mode first opens.
 * Cycles sequentially through the time-of-day greeting pool.
 * @param {Date} [date] Device local time (defaults to now).
 * @returns {string}
 */
export function buildAshleyOpeningGreeting(date = new Date()) {
  const period = getTimeOfDayPeriod(date);
  const greetings = ASHLEY_WAKE_GREETINGS[period];
  const greeting = greetings[greetingCycleIndex % greetings.length];
  greetingCycleIndex += 1;
  return greeting;
}

/**
 * First token of a display name for spoken greetings (never the full name).
 * @param {string|null|undefined} displayName
 * @returns {string|null}
 */
export function resolveAshleyFirstName(displayName) {
  if (!displayName || typeof displayName !== "string") {
    return null;
  }

  const trimmed = displayName.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes("@") && !trimmed.includes(" ")) {
    const localPart = trimmed.split("@")[0]?.trim();
    if (!localPart) {
      return null;
    }
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  }

  return trimmed.split(/\s+/)[0] || null;
}

/**
 * Time-of-day greeting with the user's first name only.
 * @param {string|null|undefined} displayName
 * @param {Date} [date]
 * @returns {string}
 */
export function buildAshleyPersonalizedGreeting(displayName, date = new Date()) {
  const period = getTimeOfDayPeriod(date);
  const salutation =
    period === "morning"
      ? "Good morning"
      : period === "afternoon"
        ? "Good afternoon"
        : "Good evening";
  const firstName = resolveAshleyFirstName(displayName);

  if (firstName) {
    return `${salutation}, ${firstName}.`;
  }

  return `${salutation}.`;
}
